/* -------------------------------------------------------------------------- */
/*            Componente SlidingPaneEntityTimelineBinnacle                    */
/* -------------------------------------------------------------------------- */
// Este componente permite ver la bitácora de cualquier entidad

import { ItemDirective, ItemsDirective, TimelineComponent } from '@syncfusion/ej2-react-layouts';
import moment from 'moment';
import { useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Send } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import SlidingPane from 'react-sliding-pane';
import { Button, Col, Row, Spinner } from 'reactstrap';
import Icon from 'utility/icomoon';
import { resolveUnitsIntlLookUp } from 'views/screens/Entities/utils';
import * as entityTypes from '../../screens/Entities/constants';
import useGetBinnacle from '../../screens/Entities/hooks/useGetBinnacle';
import useInsertBinnacleComment from '../../screens/Entities/hooks/useInsertBinnacleComment';
import TextInput from '../TextInput';

interface Props {
  entityName: any;
  entityTypeId: any;
  entityId: any;
  isOpenSlidingPane: any;
  closeSlidingPaneEntityBinnacle: any;
  width?: string;
}

const SlidingPaneEntityTimelineBinnacle = (props: Props) => {
  const {
    entityName,
    entityTypeId,
    entityId,
    isOpenSlidingPane,
    closeSlidingPaneEntityBinnacle,
    width
  } = props;
  const { control, handleSubmit, watch, reset } = useForm();
  const intl = useIntl();

  const newComment = watch('comment');
  const searchBinnacle = watch('searchBinnacle');
  const [updateBinnacle, setUpdateBinnacle] = useState(true);
  const [executeSave, setExecuteSave] = useState(false);
  const [valuesToSave, setValuesToSave] = useState<any>({});

  const { binnacleList, isFetchingBinnacle } = useGetBinnacle(
    entityTypeId,
    entityId,
    updateBinnacle
  );

  const filteredBySearchInputBinnacleList = binnacleList?.filter((binnacle) => {
    const searchTerm = searchBinnacle?.toLowerCase() || '';

    if (!searchTerm) return true;

    const comment = binnacle?.comment?.toLowerCase() || '';
    const categoryDescription = binnacle?.categoryDescription?.toLowerCase() || '';
    const completeName = binnacle?.completeName?.toLowerCase() || '';

    return (
      comment.includes(searchTerm) ||
      categoryDescription.includes(searchTerm) ||
      completeName.includes(searchTerm)
    );
  });

  const groupedBinnacleListByDate = filteredBySearchInputBinnacleList?.reduce((acc, binnacle) => {
    const date = moment(binnacle.auditDate).format('YYYY-MM-DD');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(binnacle);
    return acc;
  }, {});

  Object.keys(groupedBinnacleListByDate).forEach((date) => {
    groupedBinnacleListByDate[date].sort((a, b) => moment(b.auditDate).diff(moment(a.auditDate)));
  });

  const sortedBinnacleListDates = Object.keys(groupedBinnacleListByDate).sort((a, b) =>
    moment(b).diff(moment(a))
  );

  //HOOK GUARDADO DE COMENTARIOS
  const { submittingSave } = useInsertBinnacleComment(executeSave, valuesToSave);

  useEffect(() => {
    if (!isFetchingBinnacle && !submittingSave && entityId !== undefined && entityId !== null) {
      setUpdateBinnacle(!updateBinnacle);
    }
  }, [entityId, submittingSave]);

  useEffect(() => {
    if (!submittingSave) setValuesToSave({});
  }, [submittingSave]);

  const getBinnacleType = (binnacleType: string, binnacleCategoryId: number, entityTypeId?: number): string => {
    switch (binnacleType) {
      case 'C':
        // Comment 'C'
        return `entities.comment.category${binnacleCategoryId}`;
      case 'AM': // Audit Modify 'AM'
        // Generate entity-specific audit translation keys
        if (entityTypeId === entityTypes.ENTITY_SIM_CARDS) {
          return `auditEntities.change.simCategory${binnacleCategoryId}`;
        } else if (entityTypeId === entityTypes.ENTITY_MODULES) {
          return `auditEntities.change.deviceCategory${binnacleCategoryId}`;
        } else if (entityTypeId === entityTypes.ENTITY_SENSORS) {
          return `auditEntities.change.sensorCategory${binnacleCategoryId}`;
        } else if (entityTypeId === entityTypes.ENTITY_USERS) {
          return `auditEntities.change.userCategory${binnacleCategoryId}`;
        } else if (entityTypeId === entityTypes.ENTITY_OPERATORS) {
          return `auditEntities.change.operatorCategory${binnacleCategoryId}`;
        } else if (entityTypeId === entityTypes.ENTITY_DIVISIONS) {
          return `auditEntities.change.divisionCategory${binnacleCategoryId}`;
        } else if (entityTypeId === entityTypes.ENTITY_SUBDIVISONS) {
          return `auditEntities.change.subdivisionCategory${binnacleCategoryId}`;
        } else if (entityTypeId === entityTypes.ENTITY_AGENTS) {
          return `auditEntities.change.agentCategory${binnacleCategoryId}`;
        } else if (entityTypeId === entityTypes.ENTITY_DRIVERS) {
          return `auditEntities.change.driverCategory${binnacleCategoryId}`;
        } else if (entityTypeId === entityTypes.ENTITY_ORGANIZATIONS) {
          return `auditEntities.change.organizationCategory${binnacleCategoryId}`;
        } else if (entityTypeId === entityTypes.ENTITY_MAINTENANCE_NOTICES) {
          return `auditEntities.change.maintenanceNoticeCategory${binnacleCategoryId}`;
        } else {
          // For other entities (units, vehicles, etc.), use the generic pattern
          return `auditEntities.change.category${binnacleCategoryId}`;
        }
      default:
        // Modify 'M'
        return `entities.change.category${binnacleCategoryId}`;
    }
  };

  const resolveEntityIntlLookUp = (
    value: string,
    i18nLookup: string,
    entityTypeId: number
  ): string => {
    switch (entityTypeId) {
      case entityTypes.ENTITY_UNITS:
        return resolveUnitsIntlLookUp(intl, value, i18nLookup);
      default:
        // Para otras entidades, el valor ya viene resuelto del backend
        return value;
    }
  };

  const resolveEntityIntlDescription = (i18nDescription: string): string =>
    intl.formatMessage({ id: i18nDescription });

  const formatBinnacleLog = (binnacleLog: any): string => {
    let comment = '';
    const entityTypeId = binnacleLog.entityTypeId;
    const i18nLookup = binnacleLog.i18NLookup;
    const i18nDescription = binnacleLog.i18NDescription;
    const binnacleComment = binnacleLog.comment;

    if (!i18nLookup && !i18nDescription) return binnacleLog.comment;

    // i18n lookup
    comment = i18nLookup
      ? resolveEntityIntlLookUp(binnacleComment, i18nLookup, entityTypeId)
      : binnacleLog.comment;
    // i18n description
    if (i18nDescription) {
      comment = `${resolveEntityIntlDescription(i18nDescription)}: ${comment}`;
    }

    return comment;
  };

  const binnacleContentTemplate = (binnacleList) => (
    <div className="d-flex flex-column mb-1">
      {binnacleList.map((binnacle) => (
        <div className="change-log-header-container">
          <span className="notification-message-title">
            {intl.formatMessage({
              id: getBinnacleType(binnacle?.type, binnacle?.categoryId, entityTypeId)
            })}
          </span>
          <div className="message-subheading-content">
            <span style={{ fontSize: '12px' }}>{binnacle?.completeName}</span>
            <span className="text-divider">•</span>
            <span style={{ fontSize: '12px' }}>{moment(binnacle.auditDate).format('h:mm A')}</span>
          </div>
          <div style={{ marginTop: '4px' }}>
            <p style={{ fontSize: '14px' }}>{formatBinnacleLog(binnacle)}</p>
          </div>
        </div>
      ))}
    </div>
  );

  const formatDateBasedOnProximity = (dateString) => {
    const now = moment();
    const date = moment(dateString);

    if (date.isSame(now, 'day')) {
      return intl.formatMessage({ id: 'common.today' });
    } else if (date.isSame(now, 'week')) {
      const formattedDate = date.format('dddd');
      return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    } else {
      return date.format('LL');
    }
  };

  return entityId && entityTypeId && entityName ? (
    <SlidingPane
      className="custom-sliding-pane"
      overlayClassName="custom-sliding-pane custom-sliding-pane-no-padding"
      isOpen={isOpenSlidingPane}
      title={intl.formatMessage({ id: 'common.binnacleChangesComments' })}
      subtitle={entityName}
      width={width ?? '40rem'}
      onRequestClose={() => {
        closeSlidingPaneEntityBinnacle();
        reset();
      }}
    >
      <>
        {(isFetchingBinnacle || submittingSave) && (
          <div className="d-flex justify-content-center my-1">
            <Spinner color="primary" />
          </div>
        )}
        {binnacleList && binnacleList?.length === 0 && !isFetchingBinnacle && !submittingSave && (
          <div className="misc-inner p-2 p-sm-3">
            <div className="w-100 text-center">
              <h2 className="mb-1">
                <FormattedMessage id="routes.noComments"></FormattedMessage>
              </h2>
            </div>
          </div>
        )}
        {!isFetchingBinnacle && !submittingSave && (
          <>
            <Row className="search-bar-change-log">
              <Col sm="12">
                <Controller
                  control={control}
                  name="searchBinnacle"
                  render={({ field, fieldState }) => (
                    <TextInput
                      field={field}
                      fieldstate={fieldState}
                      type={'search'}
                      id="searchBinnacleInput"
                      prependcomponent={<Icon icon="Search" size={14} />}
                      placeholder={intl.formatMessage({ id: 'common.search' })}
                    />
                  )}
                />
              </Col>
            </Row>
            <div className="change-log-container">
              <TimelineComponent id="binnacleTimeline">
                <ItemsDirective>
                  {sortedBinnacleListDates.map((date, index) => (
                    <ItemDirective
                      key={index}
                      content={ReactDOMServer.renderToString(
                        binnacleContentTemplate(groupedBinnacleListByDate[date])
                      )}
                      oppositeContent={`<span>${formatDateBasedOnProximity(date)}</span>`}
                    />
                  ))}
                </ItemsDirective>
              </TimelineComponent>
            </div>
          </>
        )}
        <br />
        <br />
        <br />
        <br />
        <br />
        <Row className={'footer-slide-pane-comments'}>
          <Col sm="10">
            <Controller
              control={control}
              name="comment"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextInput
                  field={field}
                  fieldstate={fieldState}
                  type={'textarea'}
                  required={true}
                  id={'commentInput'}
                />
              )}
            />
          </Col>
          <Col>
            <Button.Ripple
              className={'mb-1'}
              color="primary"
              onClick={handleSubmit((values) => {
                setValuesToSave({
                  entityTypeId,
                  entityId,
                  categoryId:
                    entityTypeId === entityTypes.ENTITY_UNITS
                      ? 5
                      : entityTypeId === entityTypes.ENTITY_VEHICLES
                      ? 6
                      : entityTypeId === entityTypes.ENTITY_SIM_CARDS
                      ? 7
                      : entityTypeId === entityTypes.ENTITY_MODULES
                      ? 8
                      : entityTypeId === entityTypes.ENTITY_DRIVERS
                      ? 21
                      : entityTypeId === entityTypes.ENTITY_SERVICE_CLASSES
                      ? 41
                      : entityTypeId === entityTypes.ENTITY_ORGANIZATIONS
                      ? 55
                      : entityTypeId === entityTypes.ENTITY_USERS
                      ? 52
                      : entityTypeId === entityTypes.ENTITY_DIVISIONS
                      ? 57
                      : entityTypeId === entityTypes.ENTITY_SUBDIVISONS
                      ? 58
                      : entityTypeId === entityTypes.ENTITY_OPERATORS
                      ? 54
                      : entityTypeId === entityTypes.ENTITY_AGENTS
                      ? 59
                      : entityTypeId === entityTypes.ENTITY_SENSORS
                      ? 60
                      : 0,
                  comment: values?.comment
                });
                setExecuteSave(!executeSave);
                //setUpdateBinnacle(!updateBinnacle);
                reset();
              })}
              disabled={
                newComment == null || newComment == '' || (newComment && newComment?.length == 0)
              }
            >
              <Send size={14} />
            </Button.Ripple>
          </Col>
        </Row>
      </>
    </SlidingPane>
  ) : null;
};

SlidingPaneEntityTimelineBinnacle.defaultProps = {};

export default SlidingPaneEntityTimelineBinnacle;
